import type { AxiosError, AxiosRequestConfig } from 'axios';
import type { Key, MutatorOptions, SWRConfiguration } from 'swr';
import type { ApiModule, BaseErrorResponse, BaseMutationRequest } from '~/common/types';

import { useCallback } from 'react';

import useSWR, { useSWRConfig } from 'swr';
import useSWRMutation from 'swr/mutation';

import { useApi } from '~/common/hooks';

/**
 * Custom hook that provides a default fetcher function for making API requests.
 *
 * @returns {Object} An object containing the fetcher function.
 */
export const useDefaultFetcher = <ResponseData = unknown>(config?: AxiosRequestConfig<unknown>) => {
  /** useApi */
  const { api } = useApi();

  /** fetcher */
  return useCallback(
    (url: string) => {
      return api.request<ResponseData>({ url, ...config }).then((res) => res.data);
    },
    [api, config],
  );
};

/**
 * Custom hook that provides a default mutation function for making API requests.
 *
 * @returns {Object} An object containing the fetcher function.
 */
export const useDefaultMutation = <RequestData extends BaseMutationRequest<unknown>>(
  config?: AxiosRequestConfig<RequestData>,
) => {
  /** useApi */
  const { api } = useApi();

  /** mutation */
  return useCallback(
    (url: string, options: Readonly<{ arg: RequestData }>) => {
      const data = options.arg;

      // Replace placeholders in the URL with values from urlParams
      if (data && 'urlParams' in data && config && config.url) {
        config.url = config.url.replace(/{(\w+)}/g, (_, key) => data.urlParams?.[key] ?? `{${key}}`);
      }

      // Make the API request with the resolved URL
      return api.request({ url, ...config, data }).then((res) => res.data);
    },
    [api, config],
  );
};

/**
 * Custom hook that uses SWR (stale-while-revalidate) for data fetching.
 *
 * @param {string} key - The key used to identify the data, should use url by default.
 * @param {AxiosRequestConfig} config - The config of axios to make the request
 */
export const useFetcher = <Response = unknown>(
  key?: string | null,
  config?: AxiosRequestConfig,
  options?: SWRConfiguration,
) => {
  /** useDefaultFetcher */
  const fetcher = useDefaultFetcher<Response>(config);

  /** return */
  return useSWR<Response, AxiosError<BaseErrorResponse>, Key>(key, fetcher, { shouldRetryOnError: false, ...options });
};

/**
 * Custom hook that uses SWR Mutation (stale-while-revalidate) for data request.
 *
 * @param {string} key - The key used to identify the data, should use url by default.
 * @param {AxiosRequestConfig} config - The config of axios to make the request
 */
export const useMutation = <Request = unknown, Response = unknown>(
  key?: string | null,
  config?: AxiosRequestConfig,
) => {
  /** useDefaultMutation */
  const mutation = useDefaultMutation<BaseMutationRequest<Request>>(config);

  /** return */
  return useSWRMutation<Response, AxiosError<BaseErrorResponse>, Key, BaseMutationRequest<Request>>(key, mutation);
};

/**
 * Hook wrapper around SWR's configuration with helpers.
 */
export const useConfig = () => {
  /** config */
  const config = useSWRConfig();

  /**
   * Clear cache entries related to a specific API module.
   * @param {ApiModule} apiKey - A substring used to match cache keys.
   */
  const clearCache = useCallback(
    (apiKey: ApiModule, opt?: MutatorOptions) => {
      config.mutate((key) => typeof key === 'string' && key.includes(apiKey), undefined, opt);
    },
    [config],
  );

  return { ...config, clearCache };
};
